/* Manual call-log panel — sits under the SMS thread on the Lead Report.
 *
 * Wholesalers tap the SMS thread's "Call" button (a `tel:` link that opens
 * their phone's native dialer), then come back here to log the outcome.
 * Intentionally minimal — outcome + optional duration + optional notes —
 * because we want this to take under 10 seconds to fill out.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PhoneCall, PhoneOff, Voicemail, Phone, AlertCircle, MessageCircle,
  Loader2, Plus, Trash2, X,
} from "lucide-react";

const OUTCOMES = [
  { key: "talked",        label: "Talked",        Icon: PhoneCall,    tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { key: "voicemail",     label: "Voicemail",     Icon: Voicemail,    tone: "text-sky-700 bg-sky-50 border-sky-200" },
  { key: "no_answer",     label: "No answer",     Icon: PhoneOff,     tone: "text-slate-700 bg-slate-50 border-slate-200" },
  { key: "busy",          label: "Busy",          Icon: Phone,        tone: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "wrong_number",  label: "Wrong number",  Icon: AlertCircle,  tone: "text-rose-700 bg-rose-50 border-rose-200" },
  { key: "other",         label: "Other",         Icon: MessageCircle,tone: "text-slate-700 bg-slate-50 border-slate-200" },
];

const OUTCOME_BY_KEY = Object.fromEntries(OUTCOMES.map((o) => [o.key, o]));

function CallLogRow({ log, onDelete }) {
  const meta = OUTCOME_BY_KEY[log.outcome] || OUTCOME_BY_KEY.other;
  const { Icon, label, tone } = meta;
  const ts = log.created_at ? new Date(log.created_at).toLocaleString() : "";
  return (
    <li
      className="card-flat p-3 flex items-start gap-3"
      data-testid={`call-log-row-${log.id}`}
    >
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-semibold ${tone}`}>
        <Icon size={12} /> {label}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[var(--ink-3)] font-mono">{ts}</div>
        {log.duration_minutes != null && (
          <div className="text-xs text-[var(--ink-2)] mt-0.5">
            <span className="font-semibold">{log.duration_minutes}</span> min on the call
          </div>
        )}
        {log.notes && (
          <p className="text-sm text-[var(--ink)] mt-1 whitespace-pre-wrap break-words">
            {log.notes}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDelete(log.id)}
        className="text-[var(--ink-3)] hover:text-rose-600 transition shrink-0"
        title="Delete log"
        data-testid={`call-log-delete-${log.id}`}
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

function CallLogForm({ leadId, onSaved, onCancel }) {
  const [outcome, setOutcome] = useState("talked");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { outcome };
      const mins = Number(duration);
      if (Number.isFinite(mins) && mins > 0) body.duration_minutes = Math.round(mins);
      if (notes.trim()) body.notes = notes.trim();
      const { data } = await api.post(`/leads/${leadId}/call-logs`, body);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save call log");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card-flat p-3 mb-3 border-dashed"
      data-testid="call-log-form"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">Log a call</div>
        <button
          type="button"
          onClick={onCancel}
          className="text-[var(--ink-3)] hover:text-[var(--ink)]"
          data-testid="call-log-form-close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Outcome picker — single-tap for the fast path */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {OUTCOMES.map(({ key, label, Icon, tone }) => (
          <button
            type="button"
            key={key}
            onClick={() => setOutcome(key)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-semibold transition ${
              outcome === key
                ? tone
                : "border-[var(--line)] text-[var(--ink-2)] bg-white hover:bg-slate-50"
            }`}
            data-testid={`call-log-outcome-${key}`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <div className="md:col-span-1">
          <label className="field-label">Duration (min)</label>
          <input
            type="number"
            min="0"
            max="600"
            className="input"
            placeholder="e.g. 4"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            data-testid="call-log-duration"
          />
        </div>
        <div className="md:col-span-2">
          <label className="field-label">Notes</label>
          <input
            className="input"
            placeholder="What happened on the call?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            data-testid="call-log-notes"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onCancel} data-testid="call-log-form-cancel">
          Cancel
        </button>
        <button
          type="button"
          className="btn-money"
          onClick={save}
          disabled={busy}
          data-testid="call-log-form-save"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {busy ? "Saving" : "Save call log"}
        </button>
      </div>
    </div>
  );
}

export default function CallLogPanel({ leadId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/leads/${leadId}/call-logs`);
        if (active) setLogs(data?.items || []);
      } catch {
        // Silent — keep the panel rendered with an empty list.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [leadId]);

  const onSaved = (log) => {
    setLogs((arr) => [log, ...arr]);
    setFormOpen(false);
    toast.success("Call logged");
    window.dispatchEvent(new CustomEvent("af:followup-changed"));
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/call-logs/${id}`);
      setLogs((arr) => arr.filter((l) => l.id !== id));
      window.dispatchEvent(new CustomEvent("af:followup-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete log");
    }
  };

  return (
    <div className="card mb-5 print:hidden" data-testid="call-log-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <PhoneCall size={16} className="text-[var(--primary)]" />
          <span className="font-display font-bold text-base">Call history</span>
          <span className="text-xs text-[var(--ink-3)] tabular-nums">· {logs.length}</span>
        </div>
        {!formOpen && (
          <button
            type="button"
            className="btn-outline inline-flex items-center gap-1.5 text-xs"
            onClick={() => setFormOpen(true)}
            data-testid="call-log-open-form"
          >
            <Plus size={12} /> Log a call
          </button>
        )}
      </div>

      {formOpen && (
        <CallLogForm
          leadId={leadId}
          onSaved={onSaved}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {loading ? (
        <div className="text-center text-[var(--ink-3)] text-sm py-4">
          <Loader2 size={14} className="animate-spin inline mr-1" /> Loading…
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center text-[var(--ink-3)] text-sm py-4" data-testid="call-log-empty">
          No calls logged yet. Tap <span className="font-semibold">Log a call</span> after your next dial.
        </div>
      ) : (
        <ul className="space-y-2">
          {logs.map((log) => (
            <CallLogRow key={log.id} log={log} onDelete={onDelete} />
          ))}
        </ul>
      )}
    </div>
  );
}
